import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { longDate } from "@/lib/format";

export function DeleteAccountDialog({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const sub = user?.subscription || {};
  const premiumActive = user?.plan !== "free" && ["active", "trialing", "past_due"].includes(sub.status);
  const close = () => { setStep(1); setPassword(""); setConfirm(""); onClose(); };

  const run = async () => {
    setBusy(true);
    try {
      await api.post("/users/me/delete", { password, confirm });
      logout();
      toast.success("Sua conta e seus dados foram excluídos.");
      navigate("/entrar", { replace: true });
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="delete-account-dialog">
        <DialogTitle className="text-red-600">Excluir minha conta</DialogTitle>
        {step === 1 && (
          <>
            <DialogDescription className="text-sm leading-relaxed">
              Isto apaga <strong>permanentemente</strong>: sua conta, todas as memórias, anexos, fotos, documentos, lembretes, a Pasta Segura e o histórico. Não há como desfazer.
              {premiumActive && <span className="mt-2 block rounded-xl p-2 text-xs" style={{ background: "#FFF4E5", color: "#7A4A00" }} data-testid="delete-account-subscription-notice">Você tem uma assinatura Premium ativa{sub.current_period_end ? ` (paga até ${longDate(sub.current_period_end)})` : ""}. Ao excluir a conta, a assinatura é <strong>cancelada imediatamente</strong> no Stripe e o acesso Premium termina junto com a conta. Não há reembolso proporcional automático.</span>}
            </DialogDescription>
            <div className="flex gap-2"><button onClick={close} className="ga-btn-ghost flex-1" data-testid="delete-account-cancel">Cancelar</button><button onClick={() => setStep(2)} className="flex-1 rounded-full bg-red-500 px-4 py-3 text-sm font-semibold text-white" data-testid="delete-account-continue">Entendi, continuar</button></div>
          </>
        )}
        {step === 2 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="space-y-3">
            <DialogDescription className="text-sm">Para sua segurança, confirme sua senha.</DialogDescription>
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Sua senha" className="ga-input" data-testid="delete-account-password" />
            <div className="flex gap-2"><button type="button" onClick={close} className="ga-btn-ghost flex-1">Cancelar</button><button type="submit" disabled={password.length < 6} className="flex-1 rounded-full bg-red-500 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="delete-account-password-next">Continuar</button></div>
          </form>
        )}
        {step === 3 && (
          <form onSubmit={(e) => { e.preventDefault(); run(); }} className="space-y-3">
            <DialogDescription className="text-sm">Última confirmação: digite <strong>EXCLUIR</strong> para apagar tudo.</DialogDescription>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="EXCLUIR" className="ga-input uppercase" data-testid="delete-account-confirm-input" />
            <div className="flex gap-2"><button type="button" onClick={close} className="ga-btn-ghost flex-1">Cancelar</button><button type="submit" disabled={busy || confirm.trim().toUpperCase() !== "EXCLUIR"} className="flex-1 rounded-full bg-red-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="delete-account-final">{busy ? "Excluindo..." : "Excluir definitivamente"}</button></div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
